import { localToday } from './time.js';
import { nextSortOrder } from './indexing.js';
import { makeList } from './lists.js';

// The one shape of a new task doc. addTask / addTasks, the setup-cleanup checklists, processes,
// project scaffolding and the MCP all build through here, so every path writes the same fields.
// `scope` = the tasks already in play (for sortOrder); pass the list you're appending to.
// `task.id` must be set (or pass `newId`).
// Item ids: the caller's newId when it has one, else short ids unique within the list.
const listItemIds = (newId) => { let k = 0; return newId || (() => `i${++k}`); };

export function makeTaskDoc(task, scope = [], now = Date.now(), newId = null) {
  const id = task.id || (newId ? newId() : null);
  if (!id) throw new Error('makeTaskDoc: task.id or newId required');
  const parent = task.parentId ? scope.find((t) => t.id === task.parentId) : null;
  const sectionId = task.sectionId !== undefined ? (task.sectionId || parent?.sectionId || null) : (parent?.sectionId || null);
  return {
    id,
    title: task.title,
    description: task.description || '',
    priority: task.priority || 'medium',
    status: task.status || 'todo',
    parentId: task.parentId || null,
    projectId: task.projectId || parent?.projectId || null,
    sectionId,
    scheduledDate: task.scheduledDate === undefined ? localToday() : task.scheduledDate,
    scheduledTime: task.scheduledTime || null,
    plannedStart: Number.isFinite(task.plannedStart) ? task.plannedStart : null,   // minutes after midnight on scheduledDate (calendar)
    // A reminder is a task with a reminder time: remindTime = when (ms), remindStyle = banner | fullscreen | alarm.
    // (remindAt, older: minutes before the planned start.)
    remindTime: Number.isFinite(task.remindTime) ? task.remindTime : null,
    remindStyle: task.remindStyle || null,
    timeBlock: task.timeBlock || null,
    estimatedMinutes: task.estimatedMinutes || null,
    timeSpent: 0,
    pauseTime: 0,
    statusLog: [],
    createdAt: now,
    completedAt: null,
    moneyEarned: task.moneyEarned || 0,
    actualValue: task.actualValue || null,
    taskType: task.taskType || 'task',
    saleStatus: task.taskType === 'sale' ? (task.saleStatus || 'pending') : null,
    billable: task.billable !== undefined ? task.billable : true,
    photo: task.photo || null,
    photoCoords: task.photoCoords || null,
    isServiceItem: task.isServiceItem || false,
    requiresAfterPhoto: task.requiresAfterPhoto || false,
    afterPhoto: task.afterPhoto || null,
    afterPhotos: Array.isArray(task.afterPhotos) ? task.afterPhotos.filter(Boolean) : [],
    shots: Array.isArray(task.shots) ? task.shots.filter((x) => x && x.uri) : [],
    tags: Array.isArray(task.tags) ? task.tags.filter(Boolean) : [],
    voiceNote: task.voiceNote || null,
    notes: Array.isArray(task.notes) ? task.notes.filter(Boolean) : (task.voiceNote ? [task.voiceNote] : []),
    sortOrder: task.sortOrder ?? nextSortOrder(scope, { parentId: task.parentId || null, sectionId }),
    ...(task.assignee ? { assignee: task.assignee } : {}),
    ...(task.departmentId && !task.parentId ? { departmentId: task.departmentId } : {}),
    ...(task.departmentGuess && !task.parentId ? { departmentGuess: task.departmentGuess } : {}),   // how capture inferred it (deptinfer.js): { confidence, why, source }
    ...(task.stage ? { stage: String(task.stage).slice(0, 32) } : {}),   // boards: where its card starts (a column's + Add)
    ...(task.scope ? { scope: task.scope } : {}),   // included | extra | missed | overhead (shared/scope.js)   // department mode (taskdepartments.js); subtasks follow their parent
    // Where its reminder's jump button goes ({ type: note | routine | project | event | task, id }); default = the task.
    ...(task.opens && task.opens.type && task.opens.id ? { opens: { type: String(task.opens.type), id: String(task.opens.id) } } : {}),   // a member uid (opsMembers) — e.g. queued for Willow
    ...(task.bucket ? { bucket: task.bucket, bucketDate: task.bucketDate || localToday() } : {}),
    ...(task.processId ? { processId: task.processId, processStepId: task.processStepId || null, blockedByTaskId: task.blockedByTaskId || null } : {}),
    ...(task.runId ? { runId: task.runId } : {}),   // a routine run's step (routines.js) — date-bound, grouped as one routine card
    ...(task.quickTaskId ? { quickTaskId: String(task.quickTaskId) } : {}),   // a department quick task's instance (quicktasks.js)
    // CFO 4.2 / 4.3: the quoted quantity (a bid line's qty + unit, or typed), the price-book item, the template it came
    // from and what the template carries (suggested piece price, before photo, review, metrics to capture).
    ...(Number(task.qty) > 0 ? { qty: Number(task.qty) } : {}),
    ...(task.unit ? { unit: String(task.unit) } : {}),
    ...(task.catalogItemId ? { catalogItemId: String(task.catalogItemId) } : {}),
    ...(task.templateId ? { templateId: String(task.templateId) } : {}),
    ...(Number(task.piecePrice) > 0 ? { piecePrice: Math.round(Number(task.piecePrice) * 100) / 100 } : {}),
    ...(task.requiresBeforePhoto ? { requiresBeforePhoto: true } : {}),
    ...(task.noteOnDone ? { noteOnDone: true } : {}),   // done asks for a note (a department's task defaults, processes.js)
    ...(task.approval === 'review' ? { approval: 'review' } : {}),
    ...(Array.isArray(task.metrics) && task.metrics.length ? { metrics: task.metrics } : {}),
    // A list (shared/lists.js): items are information, not work — quick add " :: milk, eggs", a template's list.
    ...(task.list && Array.isArray(task.list.items) && task.list.items.length ? { list: makeList(task.list, listItemIds(newId)) } : {}),
  };
}
